import { Component, Input, ElementRef, ViewChild, OnInit, Output, EventEmitter } from '@angular/core';
import { InlineFieldInterfaceComponent } from '../inline-field-interface.component';
import { PartialFormField } from '../../form-fields/form-field.types';
import { RelatedToType } from 'app/shared/enums';
import { ActivatedRoute, Router } from '@angular/router';
import { Project } from 'app/modules/projects/projects.types';
import { FoldersTabService } from 'app/shared/modules/folders/folders.service';
import { FileResource } from 'app/shared/modules/folders/folders.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { UserState } from '@state/user/state';
import { Store } from '@ngxs/store';

@Component({
  templateUrl: './record-inline-field.component.html',
  styleUrls: ['./record-inline-field.component.scss'],
})
export class RecordInlineFieldComponent implements InlineFieldInterfaceComponent, OnInit {
  @ViewChild('inputControl') inputControl: ElementRef;

  @Input() model: any;
  @Input() fieldDef: any;
  @Input() data: any;
  @Input() excludeLabels: boolean = false;
  @Input() readonly: boolean = false;
  @Input() canClick: boolean = false;
  @Output() onClick: EventEmitter<PartialFormField> = new EventEmitter();
  @Output() update: EventEmitter<PartialFormField> = new EventEmitter();

  public editing: boolean = false;
  public value;
  public showTip: string;
  private _originalValue;
  private _readonly;
  public customObjType: string = RelatedToType.Contact;
  private _customObjId: number;
  isCustomObjectLookup: boolean;
  options: any[];
  filteredOptions: any[];
  private project: Project;
  fileResource: FileResource;
  uploadingFile = false;
  multipleUserIds;

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private readonly projectsService: ProjectsService,
    private readonly foldersTabService: FoldersTabService,
    private readonly store: Store,
  ) {}

  ngOnInit() {
    this.value = this.fieldDef.meta?.value_hidden ? 'REDACTED' : this.model[this.fieldDef.name];
    // we want to track the original value, so that we don't hit save to many times
    this._originalValue = this.value;
    this.showTip = this.fieldDef.meta?.calc_code;
    if (this.fieldDef.meta && this.fieldDef.meta.readonly) {
      this._readonly = this.fieldDef.meta.readonly;
    }
    if (this.readonly) {
      this._readonly = this.readonly;
    }

    const customObjType = this.fieldDef?.meta?.record_type;
    this.customObjType = customObjType;
    this.isCustomObjectLookup = this.fieldDef?.name?.startsWith('cf_linked_') ?? false;
    if ([RelatedToType.Contact, RelatedToType.Company].includes(customObjType) || this.isCustomObjectLookup) {
      if (this.value) {
        let value;
        if (this.value instanceof Array) {
          value = this.value.map((val) => val?.split('|')[1]).join(', ');
        } else {
          value = this.value.split('|');
          this._customObjId = value[0];
          this.value = value[1];
        }
      }

      this._originalValue = this.value;
      return;
    } else if (this.customObjType == RelatedToType.User) {
      this.options = this.store.selectSnapshot(UserState.getUsers);
      this.filteredOptions = this.options;
      if (this.value instanceof Array) {
        this.multipleUserIds = this.value.map((val) => +val?.split('|')[0]);
        this.value = this.value.map((val) => val?.split('|')[1]);
      } else {
        if (this.value) {
          if (this.value.includes('|')) {
            const value = this.value.split('|');
            this._customObjId = value[0];
            this.value = value[1];
          }
        }
      }
    } else if (this.customObjType === RelatedToType.FileResource) {
      if (this.value) {
        if (this.value.includes('|')) {
          const value = this.value.split('|');
          this._customObjId = Number(value[0]);
          this.value = value[1];
        }
      }
      this._originalValue = this.value;

      this.getFileResource(this.model?.root_folder_resource_id, this._customObjId);
    } else if (this.customObjType === RelatedToType.Project) {
      this.getProjectData();
    } else {
      if (this.value && typeof this.value === 'string') {
        this.value = JSON.parse(this.value);
      }
    }

    this._originalValue = this.value;
  }

  edit() {
    if (!this.editing && !this._readonly) {
      this.editing = true;
      setTimeout(() => {
        this.inputControl.nativeElement.focus();
      }, 300);
    }
  }

  onBlur($event: Event) {
    this.onSave();
    this.editing = false;
  }

  /**
   * When the model is updated after save, re-run our logic
   * @param model
   */
  updateChangedModel(model: any): void {
    this.model = model;
    //this.value = this.model[this.fieldDef.name] * 100;
    this.value = this.model[this.fieldDef.name];

    // we want to track the original value, so that we don't hit save to many times
    this._originalValue = this.value;
  }

  onSave() {
    if (this._originalValue != this.value) {
      this.updateModel();
      this.update.emit({ [this.fieldDef.name]: this.value });
    }
  }

  clickField($event) {
    this.onClick.emit($event);
  }

  updateModel() {
    this.model[this.fieldDef.name] = this.value;
    this._originalValue = this.value;
  }

  goToBaseRecord() {
    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    let vals = this.getValues();
    if (this.customObjType == RelatedToType.Contact) {
      this._router.navigate(['/contacts', vals[0]]);
    } else if (this.customObjType == RelatedToType.Company) {
      this._router.navigate(['/companies', vals[0]]);
    } else if (this.customObjType == RelatedToType.Task) {
      this._router.navigate([
        '/projects',
        this.model.pipeline_id,
        this.value.project_id,
        'tasks',
        this.value.task_list_id,
        this.value.id,
      ]);
    } else if (this.isCustomObjectLookup) {
      if (this.customObjType == RelatedToType.Property) {
        this._router.navigate(['/properties', vals[0]]);
      } else if (this.customObjType == RelatedToType.Project) {
        this._router.navigate(['/projects', this.project?.pipeline_id, vals[0]]);
      } else {
        this._router.navigate(['/custom-objects', this.data?.customObjectId, vals[0]]);
      }
    }
  }

  getValues() {
    if (this.model[this.fieldDef.name] && typeof this.model[this.fieldDef.name] === 'string')
      return this.model[this.fieldDef.name].split('|');
    else return null;
  }

  private getFileResource(folderId: number, fileId: number) {
    this.setFolderSettings();
    if (folderId && fileId)
      this.foldersTabService.getFile(folderId, fileId).subscribe((fileResource) => {
        this.fileResource = fileResource;
      });
  }

  private setFolderSettings() {
    if (this.data?.relatedToType === RelatedToType.Project) {
      this.foldersTabService.moduleId = this.model?.id;
      this.foldersTabService.moduleBaseUrl = 'projects';
    } else if (this.data?.relatedToType === RelatedToType.Property) {
      this.foldersTabService.moduleId = this.model?.id;
      this.foldersTabService.moduleBaseUrl = 'properties';
    }
  }

  private getProjectData(): void {
    const vals = this.getValues();

    if (vals.filter((x) => !x).length > 0) {
      return;
    }

    this.projectsService.getProjectById(vals[0]).subscribe((project: Project) => {
      this.project = project;
    });
  }
}
